import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Clock4 } from 'lucide-react'

import { apiErrorMessage, apiFetch } from '@/lib/api'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'

const schema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(200, 'Hasło jest za długie'),
    confirmNewPassword: z.string().min(1, 'Potwierdź nowe hasło'),
  })
  .refine((v) => v.newPassword === v.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Hasła nie są identyczne',
  })

type FormValues = z.infer<typeof schema>

export default function ResetPassword() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
  })

  // Brak tokena w URL — link niekompletny. Idź do /login z alertem.
  useEffect(() => {
    if (!token) navigate('/login?expired=1', { replace: true })
  }, [token, navigate])

  const confirm = useMutation({
    mutationFn: (values: { newPassword: string }) =>
      apiFetch<{ ok: true }>('/api/auth/password-reset-confirm', {
        method: 'POST',
        body: { token, newPassword: values.newPassword },
      }),
    onSuccess: () => {
      navigate('/login?reset=ok', { replace: true })
    },
  })

  const submitError = confirm.error ? apiErrorMessage(confirm.error) : null

  if (!token) return null

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <Clock4 className="mb-2 h-6 w-6 text-brand-accent" aria-hidden />
          <p className="text-xs uppercase tracking-widest text-text-faint">
            Dental Avenue
          </p>
          <h1 className="mt-1 text-2xl font-semibold">Ustaw nowe hasło</h1>
        </div>

        <form
          onSubmit={form.handleSubmit(({ newPassword }) =>
            confirm.mutate({ newPassword }),
          )}
          noValidate
          className="space-y-4"
        >
          <div>
            <Label htmlFor="newPassword">Nowe hasło</Label>
            <Input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              autoFocus
              invalid={!!form.formState.errors.newPassword}
              {...form.register('newPassword')}
            />
            {form.formState.errors.newPassword && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.newPassword.message}
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="confirmNewPassword">Powtórz nowe hasło</Label>
            <Input
              id="confirmNewPassword"
              type="password"
              autoComplete="new-password"
              invalid={!!form.formState.errors.confirmNewPassword}
              {...form.register('confirmNewPassword')}
            />
            {form.formState.errors.confirmNewPassword && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.confirmNewPassword.message}
              </p>
            )}
          </div>

          {submitError && (
            <Alert variant="error">
              <p>{submitError}</p>
              <p className="mt-1">
                <Link
                  to="/forgot-password"
                  className="underline hover:opacity-80"
                >
                  Wygeneruj nowy link
                </Link>
              </p>
            </Alert>
          )}

          <Button type="submit" loading={confirm.isPending} className="w-full">
            Ustaw hasło
          </Button>
        </form>
      </div>
    </main>
  )
}
